import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, Users, ChevronDown, TrendingUp, TrendingDown,
  Wallet, ArrowRight, Zap, Lock, Gift, Star, 
  CheckCircle, ArrowUpRight, Globe, Coins, Building2,
  BarChart3, Clock, ChevronRight, ExternalLink, 
  MessageCircle, Mail, Twitter, Send
} from 'lucide-react';
import { settingsApi } from '../lib/api';

const BLC_LOGO = "/blc-coin-logo.png";

// Static data outside component
const TOKENOMICS = [
  { label: 'Airdrop & ICO', percent: 30, color: 'bg-purple-500' },
  { label: 'Staking Pool', percent: 30, color: 'bg-blue-500' },
  { label: 'Exchange Listing', percent: 10, color: 'bg-green-500' },
  { label: 'Liquidity Pool', percent: 10, color: 'bg-amber-500' },
  { label: 'Core Team', percent: 20, color: 'bg-pink-500' },
];

const ROADMAP = [
  { phase: 'Phase 1', title: 'Foundation (2024)', status: 'completed', items: ['Project Vision Defined', 'Team Formation', 'BSC Research', 'Token Development'] },
  { phase: 'Phase 2', title: 'Smart Contract (2024)', status: 'completed', items: ['Contract Deployment', 'Security Audit', 'Branding Launch', 'Whitepaper v1.0'] },
  { phase: 'Phase 3', title: 'Community (2025)', status: 'completed', items: ['Staking Platform', 'Referral Program', 'Reward System', 'Whitepaper v2'] },
  { phase: 'Phase 4', title: 'Expansion (2026)', status: 'completed', items: ['BLC Stakers', 'Payment Gateway', 'Partnerships', 'Marketing'] },
  { phase: 'Phase 5', title: 'DEX Launch (Q3 2026)', status: 'active', items: ['DEX Listings', 'Farming Pools', 'Multi-Chain', 'Marketing'] },
  { phase: 'Phase 6', title: 'Wallet (Q1 2027)', status: 'upcoming', items: ['iOS & Android', 'Multi-Token', 'Swap Feature', 'Security'] },
];

const TESTIMONIALS = [
  { name: 'Michael Chen', role: 'Early Investor', image: 'https://randomuser.me/api/portraits/men/32.jpg', text: 'Belini Coin has changed my perspective on crypto staking. Consistent daily rewards.' },
  { name: 'Sarah Williams', role: 'Crypto Enthusiast', image: 'https://randomuser.me/api/portraits/women/44.jpg', text: 'The team is transparent and always delivering. Excited for the wallet!' },
  { name: 'David Kumar', role: 'Community Leader', image: 'https://randomuser.me/api/portraits/men/67.jpg', text: 'Building a team with BLC has been rewarding. Best commission structure.' },
];

const FAQS = [
  { q: 'What is Belini Coin (BLC)?', a: 'BLC is a revolutionary BEP-20 token built on BSC with DeFi capabilities and staking ecosystem.' },
  { q: 'How do I start staking?', a: 'Sign up, deposit USDT, choose a package, and start earning daily rewards automatically.' },
  { q: 'What are the staking rewards?', a: 'Rewards vary from 0.5% to 1% daily based on your package tier.' },
  { q: 'When can I withdraw?', a: 'Withdrawals are processed on designated dates. Transfer ROI to Cash wallet first.' },
  { q: 'How does referral work?', a: 'Earn instant commissions when referrals stake, with up to 6 levels of bonuses.' },
  { q: 'What is the Belini Wallet?', a: 'Upcoming multi-chain wallet with built-in swap and staking management.' },
];

const EXCHANGES = [
  { name: 'Coinstore', logo: 'https://s2.coinmarketcap.com/static/img/exchanges/64x64/1180.png', type: 'CEX' },
  { name: 'PancakeSwap', logo: 'https://cryptologos.cc/logos/pancakeswap-cake-logo.png?v=040', type: 'DEX' },
  { name: 'Uniswap', logo: 'https://cryptologos.cc/logos/uniswap-uni-logo.png?v=040', type: 'DEX' },
  { name: 'Gate.io', logo: 'https://s2.coinmarketcap.com/static/img/exchanges/64x64/302.png', type: 'CEX' },
];

export default function LandingPage() {
  const [blcPrice, setBlcPrice] = useState(0.025);
  const [cryptoPrices, setCryptoPrices] = useState([]);
  const [typedText, setTypedText] = useState('');

  const typeTexts = useRef(['Stake & Earn Daily Rewards', 'Build Your Crypto Empire', 'Join 25,000+ Investors']);
  const typingRef = useRef({ charIndex: 0, textIndex: 0, isDeleting: false });

  useEffect(() => {
    settingsApi.get().then(res => {
      setBlcPrice(res.data.settings?.blc_price || 0.025);
    }).catch(() => {});

    const fetchPrices = async () => {
      try {
        const res = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,binancecoin,solana,ripple&vs_currencies=usd&include_24hr_change=true');
        const data = await res.json();
        setCryptoPrices([
          { symbol: 'BTC', price: data.bitcoin?.usd || 78118, change: data.bitcoin?.usd_24h_change || 0 },
          { symbol: 'ETH', price: data.ethereum?.usd || 2287, change: data.ethereum?.usd_24h_change || 0 },
          { symbol: 'BNB', price: data.binancecoin?.usd || 770, change: data.binancecoin?.usd_24h_change || 0 },
          { symbol: 'SOL', price: data.solana?.usd || 102, change: data.solana?.usd_24h_change || 0 },
          { symbol: 'XRP', price: data.ripple?.usd || 1.60, change: data.ripple?.usd_24h_change || 0 },
        ]);
      } catch {
        setCryptoPrices([
          { symbol: 'BTC', price: 78118, change: -0.10 },
          { symbol: 'ETH', price: 2287, change: -1.01 },
          { symbol: 'BNB', price: 770, change: 0.35 },
          { symbol: 'SOL', price: 102, change: -0.13 },
          { symbol: 'XRP', price: 1.60, change: -2.27 },
        ]);
      }
    };
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  // Typing effect — interval-based to avoid React 18 strict mode issues
  useEffect(() => {
    const texts = typeTexts.current;
    const state = typingRef.current;
    state.charIndex = 0;
    state.textIndex = 0;
    state.isDeleting = false;
    setTypedText('');

    const tick = () => {
      const currentText = texts[state.textIndex];
      if (!state.isDeleting) {
        state.charIndex++;
        setTypedText(currentText.slice(0, state.charIndex));
        if (state.charIndex >= currentText.length) {
          state.isDeleting = true;
          clearInterval(intervalId);
          setTimeout(() => { intervalId = setInterval(tick, 50); }, 2000);
          return;
        }
      } else {
        state.charIndex--;
        setTypedText(currentText.slice(0, state.charIndex));
        if (state.charIndex <= 0) {
          state.isDeleting = false;
          state.textIndex = (state.textIndex + 1) % texts.length;
          clearInterval(intervalId);
          setTimeout(() => { intervalId = setInterval(tick, 100); }, 300);
          return;
        }
      }
    };

    let intervalId = setInterval(tick, 100);
    return () => clearInterval(intervalId);
  }, []);

  const formatPrice = (price) => {
    if (price >= 1000) return '$' + price.toLocaleString(undefined, { maximumFractionDigits: 0 });
    if (price >= 1) return '$' + price.toFixed(2);
    return '$' + price.toFixed(4);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-400 overflow-x-hidden">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(212,175,55,0.08)_0%,_transparent_50%)]" />
      </div>
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={BLC_LOGO} alt="Belini Coin" className="h-10 md:h-12" />
            <span className="text-white font-bold text-lg hidden sm:block">BELINI <span className="text-amber-400">COIN</span></span>
          </Link>
          
          <div className="hidden lg:flex items-center gap-8">
            <a href="#about" className="text-sm hover:text-amber-400 transition-colors">About</a>
            <a href="#tokenomics" className="text-sm hover:text-amber-400 transition-colors">Tokenomics</a>
            <a href="#roadmap" className="text-sm hover:text-amber-400 transition-colors">Roadmap</a>
            <a href="#faq" className="text-sm hover:text-amber-400 transition-colors">FAQ</a>
          </div>
          
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/login" className="px-4 md:px-6 py-2 text-sm text-white hover:text-amber-400" data-testid="login-btn">Login</Link>
            <Link to="/signup" className="gold-btn px-4 md:px-6 py-2.5 rounded-xl text-sm font-medium" data-testid="signup-btn">Start Staking</Link>
          </div>
        </div>
      </nav>

      {/* Live Crypto Ticker */}
      <div className="fixed top-16 md:top-20 left-0 right-0 z-40 bg-black/95 border-b border-amber-500/20 py-2.5 overflow-hidden">
        <div className="ticker-wrapper">
          <div className="ticker-content animate-ticker">
            <div className="ticker-item">
              <img src={BLC_LOGO} alt="BLC" className="w-5 h-5" />
              <span className="text-amber-400 font-bold">BLC</span>
              <span className="text-white font-mono">${blcPrice.toFixed(4)}</span>
              <span className="text-green-400 text-xs flex items-center"><TrendingUp className="w-3 h-3" /> +5.2%</span>
            </div>
            {cryptoPrices.map((c, i) => (
              <div key={i} className="ticker-item">
                <span className="text-zinc-400">{c.symbol}</span>
                <span className="text-zinc-200 font-mono">{formatPrice(c.price)}</span>
                <span className={c.change >= 0 ? 'text-green-400 text-xs flex items-center' : 'text-red-400 text-xs flex items-center'}>
                  {c.change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {c.change >= 0 ? '+' : ''}{c.change.toFixed(2)}%
                </span>
              </div>
            ))}
            <div className="ticker-item">
              <img src={BLC_LOGO} alt="BLC" className="w-5 h-5" />
              <span className="text-amber-400 font-bold">BLC</span>
              <span className="text-white font-mono">${blcPrice.toFixed(4)}</span>
              <span className="text-green-400 text-xs flex items-center"><TrendingUp className="w-3 h-3" /> +5.2%</span>
            </div>
            {cryptoPrices.map((c, i) => (
              <div key={'d' + i} className="ticker-item">
                <span className="text-zinc-400">{c.symbol}</span>
                <span className="text-zinc-200 font-mono">{formatPrice(c.price)}</span>
                <span className={c.change >= 0 ? 'text-green-400 text-xs flex items-center' : 'text-red-400 text-xs flex items-center'}>
                  {c.change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {c.change >= 0 ? '+' : ''}{c.change.toFixed(2)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative pt-44 md:pt-52 pb-20 min-h-screen flex flex-col justify-center items-center text-center px-4 md:px-6">
        <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }} className="relative mb-8">
          <div className="absolute inset-0 bg-amber-500/20 rounded-full blur-3xl animate-pulse" />
          <motion.img src={BLC_LOGO} alt="Belini Coin" className="w-32 h-32 md:w-40 md:h-40 relative z-10" animate={{ y: [0, -15, 0] }} transition={{ duration: 3, repeat: Infinity }} />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="inline-flex items-center gap-2 px-5 py-2 rounded-full glass border-green-500/30 text-green-300 text-xs font-medium mb-6">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          Live on Binance Smart Chain (BEP-20)
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-4 max-w-5xl">
          <span className="bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 bg-clip-text text-transparent">The Future of Digital Finance</span>
        </motion.h1>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="text-xl md:text-2xl text-white mb-6 h-8">
          {typedText}<span className="animate-pulse">|</span>
        </motion.div>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="text-base md:text-lg text-zinc-400 max-w-2xl mb-10 px-4">
          Belini Coin (BLC) is a revolutionary BEP-20 token powering the next generation of decentralized finance.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="flex flex-col sm:flex-row items-center gap-4 mb-12">
          <Link to="/signup" className="w-full sm:w-auto px-10 py-4 rounded-xl gold-btn flex items-center justify-center gap-2 text-lg font-semibold" data-testid="hero-cta-btn">
            Start Staking Now <ArrowRight className="w-5 h-5" />
          </Link>
          <a href="#about" className="w-full sm:w-auto px-10 py-4 rounded-xl glass text-white font-medium flex items-center justify-center gap-2 border border-white/10">
            Learn More <ChevronDown className="w-5 h-5 animate-bounce" />
          </a>
        </motion.div>

        {/* Hero Stats */}
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }} className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 w-full max-w-4xl">
          <div className="glass p-5 rounded-xl text-center border border-white/5">
            <Coins className="w-6 h-6 text-amber-400 mx-auto mb-2" />
            <p className="text-xl md:text-2xl font-bold text-white">${blcPrice.toFixed(4)}</p>
            <p className="text-xs text-zinc-500 mt-1">BLC Price</p>
          </div>
          <div className="glass p-5 rounded-xl text-center border border-white/5">
            <BarChart3 className="w-6 h-6 text-green-400 mx-auto mb-2" />
            <p className="text-xl md:text-2xl font-bold text-white">$11M+</p>
            <p className="text-xs text-zinc-500 mt-1">Market Cap</p>
          </div>
          <div className="glass p-5 rounded-xl text-center border border-white/5">
            <Globe className="w-6 h-6 text-blue-400 mx-auto mb-2" />
            <p className="text-xl md:text-2xl font-bold text-white">600M BLC</p>
            <p className="text-xs text-zinc-500 mt-1">Total Supply</p>
          </div>
          <div className="glass p-5 rounded-xl text-center border border-white/5">
            <Users className="w-6 h-6 text-purple-400 mx-auto mb-2" />
            <p className="text-xl md:text-2xl font-bold text-white">5,000+</p>
            <p className="text-xs text-zinc-500 mt-1">Active Stakers</p>
          </div>
        </motion.div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">About <span className="gold-gradient-text">Belini Coin</span></h2>
            <p className="text-zinc-400 max-w-3xl mx-auto text-lg">Revolutionary BEP-20 token on BSC powering the future of decentralized finance.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="glass p-6 rounded-xl border border-amber-500/20">
                <h3 className="text-xl font-semibold text-white mb-3 flex items-center gap-2"><Shield className="w-5 h-5 text-amber-400" />BSC Network</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-zinc-500">Network</p><p className="text-white font-medium">Binance Smart Chain</p></div>
                  <div><p className="text-zinc-500">Token Standard</p><p className="text-white font-medium">BEP-20</p></div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 rounded-lg hover:bg-white/5">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center"><Zap className="w-5 h-5 text-amber-400" /></div>
                  <div><h4 className="text-white font-medium">Low Transaction Fees</h4><p className="text-sm text-zinc-500">Minimal gas fees on BSC</p></div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg hover:bg-white/5">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center"><Lock className="w-5 h-5 text-amber-400" /></div>
                  <div><h4 className="text-white font-medium">Secure & Audited</h4><p className="text-sm text-zinc-500">Smart contract verified</p></div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg hover:bg-white/5">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center"><Gift className="w-5 h-5 text-amber-400" /></div>
                  <div><h4 className="text-white font-medium">Staking Rewards</h4><p className="text-sm text-zinc-500">Up to 1% daily</p></div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg hover:bg-white/5">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center"><Users className="w-5 h-5 text-amber-400" /></div>
                  <div><h4 className="text-white font-medium">Referral Program</h4><p className="text-sm text-zinc-500">Up to 6 levels</p></div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-purple-500/20 rounded-3xl blur-3xl" />
              <div className="relative glass p-8 rounded-3xl border border-white/10">
                <div className="flex justify-center mb-6">
                  <motion.img src={BLC_LOGO} alt="BLC" className="w-32 h-32" animate={{ y: [0, -10, 0] }} transition={{ duration: 3, repeat: Infinity }} />
                </div>
                <div className="text-center space-y-4">
                  <h3 className="text-2xl font-bold text-white">Belini Coin</h3>
                  <p className="text-amber-400 font-mono text-lg">BLC</p>
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
                    <div><p className="text-zinc-500 text-sm">Current Price</p><p className="text-2xl font-bold text-green-400">${blcPrice.toFixed(4)}</p></div>
                    <div><p className="text-zinc-500 text-sm">24h Change</p><p className="text-2xl font-bold text-green-400">+5.2%</p></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tokenomics Section */}
      <section id="tokenomics" className="py-24 px-4 md:px-6 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">BLC <span className="gold-gradient-text">Tokenomics</span></h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">Token distribution for sustainable growth.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative flex justify-center">
              <div className="relative w-64 h-64 md:w-80 md:h-80">
                <div className="absolute inset-0 bg-amber-500/20 rounded-full blur-3xl animate-pulse" />
                <img src={BLC_LOGO} alt="BLC" className="w-36 h-36 md:w-44 md:h-44 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 drop-shadow-2xl" />
                <div className="absolute inset-0 rounded-full border-8 border-purple-500/30" />
                <div className="absolute inset-4 rounded-full border-8 border-blue-500/30" />
                <div className="absolute inset-8 rounded-full border-8 border-green-500/30" />
                <div className="absolute inset-12 rounded-full border-8 border-amber-500/30" />
              </div>
            </div>

            <div className="space-y-4">
              {TOKENOMICS.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4 glass rounded-xl">
                  <div className={`w-4 h-4 rounded-full ${item.color}`} />
                  <div className="flex-1"><p className="text-white font-medium">{item.label}</p></div>
                  <p className="text-2xl font-bold text-white">{item.percent}%</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section id="roadmap" className="py-24 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Project <span className="gold-gradient-text">Roadmap</span></h2>
          </motion.div>

          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-amber-500 via-purple-500 to-blue-500" />
            <div className="space-y-8">
              {ROADMAP.map((phase, idx) => (
                <div key={idx} className="relative flex items-start gap-8 ml-12 md:ml-0">
                  <div className="absolute left-[-32px] md:left-1/2 md:-translate-x-1/2 z-10">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${phase.status === 'completed' ? 'bg-green-500' : phase.status === 'active' ? 'bg-amber-500 animate-pulse' : 'bg-zinc-700'}`}>
                      {phase.status === 'completed' ? <CheckCircle className="w-5 h-5 text-white" /> : phase.status === 'active' ? <Clock className="w-4 h-4 text-black" /> : <ChevronRight className="w-4 h-4 text-white" />}
                    </div>
                  </div>
                  <div className="flex-1 md:w-1/2 md:odd:ml-auto md:even:mr-auto">
                    <div className={`glass p-6 rounded-xl border ${phase.status === 'active' ? 'border-amber-500/50' : 'border-white/10'}`}>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${phase.status === 'completed' ? 'bg-green-500/20 text-green-400' : phase.status === 'active' ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-700/50 text-zinc-400'}`}>{phase.phase}</span>
                      <h3 className="text-lg font-semibold text-white mb-3">{phase.title}</h3>
                      <ul className="space-y-2">
                        {phase.items.map((item, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-zinc-400">
                            <CheckCircle className={`w-4 h-4 ${phase.status === 'completed' ? 'text-green-500' : 'text-zinc-600'}`} />{item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="py-24 px-4 md:px-6 bg-gradient-to-b from-transparent via-amber-500/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Coming <span className="gold-gradient-text">Soon</span></h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="glass p-8 rounded-2xl border border-amber-500/20">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-amber-500/10 flex items-center justify-center"><Wallet className="w-8 h-8 text-amber-400" /></div>
                <div><h3 className="text-2xl font-bold text-white">Belini Wallet</h3><span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">Coming Q1 2027</span></div>
              </div>
              <p className="text-zinc-400 mb-6">Secure multi-chain wallet supporting BLC and major tokens.</p>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-sm text-zinc-300"><CheckCircle className="w-4 h-4 text-green-500" />iOS & Android Apps</li>
                <li className="flex items-center gap-2 text-sm text-zinc-300"><CheckCircle className="w-4 h-4 text-green-500" />Multi-Token Support</li>
                <li className="flex items-center gap-2 text-sm text-zinc-300"><CheckCircle className="w-4 h-4 text-green-500" />Built-in Swap</li>
              </ul>
            </div>

            <div className="glass p-8 rounded-2xl border border-purple-500/20">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-purple-500/10 flex items-center justify-center"><Building2 className="w-8 h-8 text-purple-400" /></div>
                <div><h3 className="text-2xl font-bold text-white">Belini Exchange</h3><span className="text-xs text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded-full">Coming 2027+</span></div>
              </div>
              <p className="text-zinc-400 mb-6">Full-featured exchange with advanced trading.</p>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-sm text-zinc-300"><CheckCircle className="w-4 h-4 text-green-500" />Spot & Margin Trading</li>
                <li className="flex items-center gap-2 text-sm text-zinc-300"><CheckCircle className="w-4 h-4 text-green-500" />Liquidity Mining</li>
                <li className="flex items-center gap-2 text-sm text-zinc-300"><CheckCircle className="w-4 h-4 text-green-500" />Low Fees</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Exchanges Section */}
      <section id="exchanges" className="py-24 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Exchange <span className="gold-gradient-text">Listings</span></h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {EXCHANGES.map((ex, idx) => (
              <div key={idx} className="glass p-6 rounded-xl text-center border border-white/10 hover:border-amber-500/30 transition-all">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center p-3">
                  <img src={ex.logo} alt={ex.name} className="w-full h-full object-contain" />
                </div>
                <h3 className="text-white font-medium mb-1">{ex.name}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full ${ex.type === 'DEX' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}`}>{ex.type}</span>
                <p className="text-xs text-zinc-500 mt-2">Coming Soon</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-24 px-4 md:px-6 bg-gradient-to-b from-transparent via-blue-500/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Key <span className="gold-gradient-text">Metrics</span></h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <div className="glass p-6 rounded-xl text-center"><Users className="w-8 h-8 text-amber-400 mx-auto mb-3" /><p className="text-3xl font-bold text-white">25,000+</p><p className="text-xs text-zinc-500 mt-2">Community</p></div>
            <div className="glass p-6 rounded-xl text-center"><Wallet className="w-8 h-8 text-green-400 mx-auto mb-3" /><p className="text-3xl font-bold text-white">10,000+</p><p className="text-xs text-zinc-500 mt-2">Wallets</p></div>
            <div className="glass p-6 rounded-xl text-center"><Coins className="w-8 h-8 text-blue-400 mx-auto mb-3" /><p className="text-3xl font-bold text-white">5,000+</p><p className="text-xs text-zinc-500 mt-2">Stakers</p></div>
            <div className="glass p-6 rounded-xl text-center"><BarChart3 className="w-8 h-8 text-purple-400 mx-auto mb-3" /><p className="text-3xl font-bold text-white">50,000+</p><p className="text-xs text-zinc-500 mt-2">Transactions</p></div>
            <div className="glass p-6 rounded-xl text-center"><Zap className="w-8 h-8 text-red-400 mx-auto mb-3" /><p className="text-3xl font-bold text-white">10M</p><p className="text-xs text-zinc-500 mt-2">Burned</p></div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Community <span className="gold-gradient-text">Voices</span></h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, idx) => (
              <div key={idx} className="glass p-6 rounded-xl border border-white/10">
                <div className="flex items-center gap-4 mb-4">
                  <img src={t.image} alt={t.name} className="w-14 h-14 rounded-full object-cover border-2 border-amber-500/30" />
                  <div><h4 className="text-white font-medium">{t.name}</h4><p className="text-xs text-amber-400">{t.role}</p></div>
                </div>
                <div className="flex gap-1 mb-3">{[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />)}</div>
                <p className="text-zinc-400 text-sm">"{t.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-4 md:px-6 bg-gradient-to-b from-transparent via-amber-500/5 to-transparent">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Frequently Asked <span className="gold-gradient-text">Questions</span></h2>
          </motion.div>

          <div className="space-y-4">
            {FAQS.map((faq, idx) => (
              <details key={idx} className="group glass rounded-xl border border-white/10">
                <summary className="flex justify-between items-center cursor-pointer list-none p-6 text-white font-medium">
                  <span className="pr-4">{faq.q}</span>
                  <ChevronDown className="w-5 h-5 text-amber-400 group-open:rotate-180 transition-transform" />
                </summary>
                <div className="text-zinc-400 px-6 pb-6 pt-0 text-sm border-t border-white/5">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative glass p-10 md:p-16 rounded-3xl text-center overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-purple-500/10" />
            <div className="relative z-10">
              <motion.img src={BLC_LOGO} alt="BLC" className="w-20 h-20 mx-auto mb-6" animate={{ y: [0, -10, 0] }} transition={{ duration: 2, repeat: Infinity }} />
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">Join the Belini Revolution</h2>
              <p className="text-zinc-400 mb-8 max-w-xl mx-auto text-lg">Start your journey towards financial freedom. Stake BLC today.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/signup" className="w-full sm:w-auto px-10 py-4 rounded-xl gold-btn text-lg font-semibold flex items-center justify-center gap-2">Start Staking <ArrowUpRight className="w-5 h-5" /></Link>
                <a href="#about" className="w-full sm:w-auto px-10 py-4 rounded-xl glass text-white font-medium flex items-center justify-center gap-2">Whitepaper <ExternalLink className="w-4 h-4" /></a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4"><img src={BLC_LOGO} alt="Belini Coin" className="h-12" /><span className="text-white font-bold text-lg">Belini Coin</span></div>
              <p className="text-zinc-500 text-sm">Powering the future of DeFi on BSC.</p>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#about" className="text-zinc-500 hover:text-amber-400">About BLC</a></li>
                <li><a href="#tokenomics" className="text-zinc-500 hover:text-amber-400">Tokenomics</a></li>
                <li><a href="#roadmap" className="text-zinc-500 hover:text-amber-400">Roadmap</a></li>
                <li><Link to="/signup" className="text-zinc-500 hover:text-amber-400">Stake BLC</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="text-zinc-500 hover:text-amber-400">Whitepaper</a></li>
                <li><a href="#" className="text-zinc-500 hover:text-amber-400">Contract</a></li>
                <li><a href="#" className="text-zinc-500 hover:text-amber-400">Audit</a></li>
                <li><a href="#faq" className="text-zinc-500 hover:text-amber-400">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4">Stay Updated</h4>
              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 rounded-full glass flex items-center justify-center text-zinc-400 hover:text-amber-400"><Twitter className="w-5 h-5" /></a>
                <a href="#" className="w-10 h-10 rounded-full glass flex items-center justify-center text-zinc-400 hover:text-amber-400"><Send className="w-5 h-5" /></a>
                <a href="#" className="w-10 h-10 rounded-full glass flex items-center justify-center text-zinc-400 hover:text-amber-400"><MessageCircle className="w-5 h-5" /></a>
                <a href="#" className="w-10 h-10 rounded-full glass flex items-center justify-center text-zinc-400 hover:text-amber-400"><Mail className="w-5 h-5" /></a>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-zinc-600">© 2026 Belini Coin. All rights reserved.</p>
            <div className="flex gap-6 text-xs text-zinc-500">
              <a href="#" className="hover:text-white">Terms</a>
              <a href="#" className="hover:text-white">Privacy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
